import type { ExperienceItem } from "@/lib/types";
import { isUserFacingValidUrl } from "@/lib/experiences/linkIntegrity";
import {
  getExperienceImageFallback,
  getExperienceImageAlt,
} from "./experienceImage";
import { getItemActivityFamilies } from "./rheaRecommendation";

/**
 * mediaRelevance.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Marketplace Media Relevance & Visual Trust V1.
 *
 * Production concern: a card can render an image that's visually incoherent
 * with the activity it's showing (e.g. a Segway tour with a nightlife/party
 * photo). No vision model or LLM is available to inspect what a real
 * provider photo actually depicts — so this module never second-guesses a
 * genuinely valid provider photo's CONTENT. What it does do:
 *
 *   1. Objective validity check (already existed): a missing/broken/non-
 *      https image_url is never used — same rule as before, just named.
 *   2. NEW: a self-contradiction check on the provider's OWN data. When an
 *      item's title clearly names one specific activity family (e.g.
 *      "estadio"/"Bernabéu" -> stadium) while its own backend `category`
 *      independently resolves to pub_crawl/nightlife (getItemActivityFamilies
 *      folds category="nightlife" into pub_crawl — see rheaRecommendation.ts),
 *      that's a genuine, observable inconsistency in the PROVIDER'S OWN
 *      metadata — not a guess about the photo. When this happens, the
 *      category-driven image association isn't trusted, and the fallback
 *      picks a pool matched to the more specific, more reliable
 *      title-derived family instead.
 * Deterministic, no LLM/vision model, no per-destination special-casing.
 * Reuses the 8-family activity-intent system from rheaRecommendation.ts —
 * this is the ONLY place that keyword table is duplicated (it isn't; it's
 * imported), so there is no second, divergent keyword list to maintain.
 *
 * Never removes/hides a product for image reasons — only ever changes which
 * image is shown. The product, its booking_url, and its provider status are
 * completely untouched by anything in this module.
 */

export type ImageRiskLevel = "none" | "invalid_url" | "family_conflict";

/**
 * A specific activity family that a fallback image pool can honestly depict.
 * segway/scooter/bike share one pool (urban mobility has no dedicated
 * curated pool yet — "sports" ["outdoor adventure / urban sport"] is the
 * closest honest existing match); tapas/cooking share "food"; the rest map
 * 1:1. Every poolKey below resolves to an EXISTING, already-verified image
 * in lib/experiences/visuals.ts — this module invents zero new photos.
 */
export const ACTIVITY_FAMILY_IMAGE_MAPPING: Readonly<Record<string, { poolKey: string; altLabel: string }>> = {
  segway:    { poolKey: "sports",    altLabel: "movilidad urbana" },
  scooter:   { poolKey: "sports",    altLabel: "movilidad urbana" },
  bike:      { poolKey: "sports",    altLabel: "movilidad urbana" },
  tapas:     { poolKey: "food",      altLabel: "gastronomía local" },
  cooking:   { poolKey: "food",      altLabel: "gastronomía local" },
  flamenco:  { poolKey: "music",     altLabel: "espectáculo cultural" },
  stadium:   { poolKey: "stadium",   altLabel: "estadio" },
  pub_crawl: { poolKey: "nightlife", altLabel: "vida nocturna" },
};

// Stable iteration order — the same item always resolves to the same family
// when it matches more than one (e.g. the real "category=nightlife, title
// mentions Bernabéu" item matches both stadium and pub_crawl; stadium comes
// first here because the TITLE signal is more specific/reliable than the
// category-derived pub_crawl signal for picking a fallback image).
export const ACTIVITY_FAMILY_PRIORITY: readonly string[] = [
  "segway", "scooter", "bike", "tapas", "cooking", "flamenco", "stadium", "pub_crawl",
];

// Geo & Media Relevance Hardening V1 — `tags` is a noisy, multi-value Viator
// field (real example: a Bernabéu/stadium tour carrying tags=["museum",
// "nightlife"]) — far less authoritative than `category` (a single value
// Viator itself picks as the product's primary classification). Media
// decisions never let a secondary tag override an otherwise-clean title/
// category read, so every family lookup in this file passes
// includePubCrawlTags: false — category alone still folds into pub_crawl.
function itemFamiliesForImage(exp: ExperienceItem): Set<string> {
  return getItemActivityFamilies(exp, { includePubCrawlTags: false });
}

function pickImageFamily(exp: ExperienceItem): string | null {
  const families = itemFamiliesForImage(exp);
  for (const family of ACTIVITY_FAMILY_PRIORITY) {
    if (families.has(family)) return family;
  }
  return null;
}

/**
 * Classifies the risk of trusting exp.image_url as-is. See module doc for
 * what each level means and why it's never based on inspecting the photo's
 * actual content.
 */
export function detectImageRisk(exp: ExperienceItem, rawQuery?: string | null): ImageRiskLevel {
  void rawQuery;
  if (!exp.image_url || !isUserFacingValidUrl(exp.image_url)) return "invalid_url";

  const itemFamilies = itemFamiliesForImage(exp);
  // Self-contradiction in the provider's own title vs. category signal —
  // e.g. category="nightlife" (-> pub_crawl) but the title clearly names a
  // different specific family (e.g. stadium). Never derived from `tags` —
  // see itemFamiliesForImage's own doc above.
  const selfContradictory =
    itemFamilies.has("pub_crawl") &&
    [...itemFamilies].some(f => f !== "pub_crawl");
  if (selfContradictory) return "family_conflict";

  return "none";
}

/** True when exp.image_url should be rendered as-is. */
export function shouldUseProviderImage(exp: ExperienceItem, rawQuery?: string | null): boolean {
  return detectImageRisk(exp, rawQuery) === "none";
}

/**
 * The single decision every marketplace surface should call for an item's
 * image — same (exp, rawQuery, cityHint) always resolves to the same URL,
 * so Row/Shelf/Drawer/ComparisonDrawer render the identical choice for the
 * identical item.
 */
export function getTrustedExperienceImage(
  exp: ExperienceItem,
  rawQuery?: string | null,
  cityHint?: string | null,
): string | null {
  if (shouldUseProviderImage(exp, rawQuery)) return exp.image_url ?? null;
  const family = pickImageFamily(exp);
  const mapping = family ? ACTIVITY_FAMILY_IMAGE_MAPPING[family] : null;
  return getExperienceImageFallback(exp, cityHint, mapping?.poolKey ?? null);
}

/**
 * The fallback image alone, ignoring risk classification — for the runtime
 * <img onError> case: the browser already told us the current src (real or
 * fallback) failed to load, so there's nothing left to classify, only a
 * next candidate to pick. Same family-aware pool selection as
 * getTrustedExperienceImage.
 */
export function getTrustedExperienceImageFallback(
  exp: ExperienceItem,
  cityHint?: string | null,
): string | null {
  const family = pickImageFamily(exp);
  const mapping = family ? ACTIVITY_FAMILY_IMAGE_MAPPING[family] : null;
  return getExperienceImageFallback(exp, cityHint, mapping?.poolKey ?? null);
}

/** Honest alt text companion to getTrustedExperienceImage — same rules as
 *  getExperienceImageAlt (real photo -> title, fallback -> "Imagen
 *  ilustrativa de …"), but using the more specific family label when a
 *  family-driven fallback was actually selected. */
export function getTrustedExperienceImageAlt(
  exp: ExperienceItem,
  rawQuery?: string | null,
  displayTitle?: string,
): string {
  const isRealPhoto = shouldUseProviderImage(exp, rawQuery);
  const family = isRealPhoto ? null : pickImageFamily(exp);
  const altLabel = family ? ACTIVITY_FAMILY_IMAGE_MAPPING[family].altLabel : null;
  return getExperienceImageAlt(exp, isRealPhoto, displayTitle, altLabel);
}
